import { ActivityType } from "@repo/api/client";
import { Flex, Text } from "@repo/ui";
import {
	CommentActivityLog,
	ErrorActivityLog,
	InlineActivityLog,
	SpruceMessageActivityLog,
} from "../components/ActivityLog";
import { RunningJobBanner } from "../components/RunningJobBanner";
import { JobType } from "@repo/db/client";
import { ToolCallView } from "../components/assistant/tools/ToolCallView";

export function UIKitRoute() {
	return (
		<Flex column gap="xl" padding="xl" overflow="y" flex>
			<Text size="xl" weight="semibold">
				UI Kit
			</Text>

			{/* Empty State */}
			<Flex column gap="md">
				<Text size="lg" weight="semibold">
					Empty State
				</Text>
				<Text size="sm" align="center" italic color="sand11">
					No activity yet
				</Text>
			</Flex>

			{/* Inline Activity Logs */}
			<Flex column gap="md">
				<Text size="lg" weight="semibold">
					Inline Activity Logs
				</Text>
				<InlineActivityLog
					icon="CircleCheckBig"
					text="Referral sent"
					color="green11"
					timestamp={new Date()}
					summary="Sent to Dr. Smith at Austin Cardiology"
				/>
				<InlineActivityLog
					icon="Search"
					text="Found 5 providers"
					color="blue11"
					timestamp={new Date()}
					summary="Cardiology in Austin, TX"
				/>
				<InlineActivityLog
					icon="ShieldCheck"
					text="Verified eligibility"
					color="green11"
					timestamp={new Date()}
				/>
			</Flex>

			{/* Comment Activity Log */}
			<Flex column gap="md">
				<Text size="lg" weight="semibold">
					Comment Activity Log
				</Text>
				<CommentActivityLog
					content="Please follow up with the patient regarding their appointment availability."
					creatorName="Sarah Johnson"
					timestamp={new Date()}
				/>
			</Flex>

			{/* Spruce Message Activity Logs */}
			<Flex column gap="md">
				<Text size="lg" weight="semibold">
					Spruce Message Activity Logs
				</Text>
				<SpruceMessageActivityLog
					messageText="Hi, I wanted to confirm your appointment for next Tuesday at 2pm. Please let me know if this time still works for you."
					direction="sent"
					timestamp={new Date()}
				/>
				<SpruceMessageActivityLog
					messageText="Yes, that works great! Thank you for confirming."
					direction="received"
					timestamp={new Date()}
				/>
			</Flex>

			{/* Error Activity Logs */}
			<Flex column gap="md">
				<Text size="lg" weight="semibold">
					Error Activity Logs
				</Text>
				<ErrorActivityLog
					payload={{
						type: ActivityType.Error,
						internalError:
							"Network timeout while connecting to provider portal",
						toolName: "submit_referral",
						toolInput: { providerId: "123", patientId: "456" },
						isUserActionable: true,
					}}
					content="Unable to submit referral due to connection issue. Please try again."
					timestamp={new Date()}
					isUserActionable={true}
				/>
				<ErrorActivityLog
					payload={{
						type: ActivityType.Error,
						internalError: "Database connection failed",
						isUserActionable: false,
					}}
					content="An unexpected error occurred while processing your request."
					timestamp={new Date()}
					isUserActionable={false}
				/>
				<ErrorActivityLog
					payload={{
						type: ActivityType.Error,
						internalError: "Browser automation failed",
						isUserActionable: true,
					}}
					content="Failed to submit prior authorization form."
					timestamp={new Date()}
					isUserActionable={true}
					mediaItem={{
						bucket: "screenshots",
						mediaUrl: "error-screenshot.png",
					}}
					onMediaClick={() => alert("View screenshot clicked")}
				/>
			</Flex>

			{/* Running Job Banners */}
			<Flex column gap="md">
				<Text size="lg" weight="semibold">
					Running Job Banners
				</Text>
				<RunningJobBanner
					jobType={JobType.AgentNextStep}
					startedTime={new Date()}
					reasoning="Analyzing patient information to determine the best provider options based on insurance coverage and location preferences."
				/>
				<RunningJobBanner
					jobType={JobType.SearchProviders}
					startedTime={new Date(Date.now() - 2 * 60 * 1000)} // 2 minutes ago
					reasoning="Searching for cardiologists within 10 miles of patient's address who accept Blue Cross Blue Shield insurance."
				/>
				<RunningJobBanner
					jobType={JobType.AttachRecordsSendReferral}
					startedTime={new Date(Date.now() - 5 * 60 * 1000)} // 5 minutes ago
				/>
			</Flex>

			{/* Reasoning Display */}
			<Flex column gap="md">
				<Text size="lg" weight="semibold">
					Reasoning Text
				</Text>
				<Flex column gap="xs">
					<Text size="sm" color="sand11">
						<Text weight="semibold">Reasoning: </Text>
						The patient requires a specialist consultation within the next two
						weeks due to the urgency of their condition. I'm prioritizing
						providers who have availability and accept their insurance.
					</Text>
				</Flex>
			</Flex>

			{/* Assistant Tool Call Blocks */}
			<Flex column gap="md">
				<Text size="lg" weight="semibold">
					Assistant Tool Call Blocks
				</Text>

				{/* Loading state */}
				<ToolCallView
					part={{
						type: "tool-search_healthcare_providers",
						toolCallId: "call-1",
						state: "input-available",
						input: {},
					}}
				/>

				{/* Success state */}
				<ToolCallView
					part={{
						type: "tool-search_healthcare_providers",
						toolCallId: "call-2",
						state: "output-available",
						input: {},
						output: {
							success: true,
							message: "Found 15 providers",
							total: 15,
							providers: [],
						},
					}}
				/>

				{/* No results state */}
				<ToolCallView
					part={{
						type: "tool-search_healthcare_providers",
						toolCallId: "call-3",
						state: "output-available",
						input: {},
						output: {
							success: true,
							message: "No providers found",
							total: 0,
							providers: [],
						},
					}}
				/>

				{/* Call provider loading */}
				<ToolCallView
					part={{
						type: "tool-call_provider",
						toolCallId: "call-4",
						state: "input-available",
						input: {
							phoneNumber: "555-0123",
							query: "Check appointment availability",
						},
					}}
				/>

				{/* Send message loading */}
				<ToolCallView
					part={{
						type: "tool-send_message_to_patient",
						toolCallId: "call-5",
						state: "input-available",
						input: {
							message: "Your appointment has been scheduled.",
						},
					}}
				/>

				{/* Check eligibility loading */}
				<ToolCallView
					part={{
						type: "tool-check_patient_eligibility",
						toolCallId: "call-6",
						state: "input-available",
						input: {},
					}}
				/>
			</Flex>
		</Flex>
	);
}
